import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Award, CheckCircle, FileText, Eye, Shield, Zap, ArrowRight, BookOpen, Send, Mail, Phone, MapPin, Facebook, Twitter, Linkedin, ChevronDown, Sparkles } from 'lucide-react';
import { imageAssets } from '../assets/imageAssets';
import { ImageWithFallback } from './figma/ImageWithFallback';
import ScreenSplash from './ui/ScreenSplash';

export default function LandingPage() {
  // Splash/intro animation timing (not a loading screen state)
  const splashAnimationDuration = {
    logoFlip: '3s',
    titleBeat: '1.3s',
    categoriesFlip: '5s',
  };
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowSplash(false), 1400);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <ScreenSplash
        show={showSplash}
        title="Welcome"
        subtitle="IMDC Creative Works Submission System"
      />

      <style>{`
        @keyframes combinedLogoFlip {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }

        .logo-face {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        @keyframes heartBeat {
          0% { transform: scale(1); }
          14% { transform: scale(1.08); }
          28% { transform: scale(1); }
          42% { transform: scale(1.08); }
          70% { transform: scale(1); }
        }

        .animate__animated {
          animation-duration: ${splashAnimationDuration.titleBeat};
          animation-fill-mode: both;
        }

        .animate__heartBeat {
          animation-name: heartBeat;
          animation-iteration-count: infinite;
          transform-origin: center;
        }

        @keyframes categorySyncFlip {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }

        .category-sync-flip {
          animation: categorySyncFlip ${splashAnimationDuration.categoriesFlip} linear infinite;
          transform-style: preserve-3d;
        }

        .category-face {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
      `}</style>

      {/* Full-screen image background sized for website viewport */}
      <div className="fixed inset-0 z-0">
        <ImageWithFallback
          src={imageAssets.creativeBackground}
          alt="Creative Works Background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60" />
      </div>
      
      {/* Content Wrapper */}
      <div className={`relative z-10 transition-opacity duration-700 ${showSplash ? 'opacity-0' : 'opacity-100'}`}>
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-xl border-b border-white/20 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              {/* One circle: front IMDC, back BISU */}
              <div className="[perspective:1000px]">
                <div
                  className="relative w-14 h-14"
                  style={{
                    animation: `combinedLogoFlip ${splashAnimationDuration.logoFlip} linear infinite`,
                    transformStyle: 'preserve-3d',
                  }}
                >
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 blur-md opacity-30"></div>

                  <div className="logo-face absolute inset-0">
                    <img
                      src={imageAssets.imdcLogo}
                      alt="IMDC Creative Works Logo"
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-lg"
                    />
                  </div>

                  <div className="logo-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <img
                      src={imageAssets.bisuLogo}
                      alt="BISU Logo"
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-lg"
                    />
                  </div>
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  IMDC Creative Works System
                </div>
                <div className="text-xs text-gray-500 font-medium tracking-wide">BOHOL ISLAND STATE UNIVERSITY</div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="border-2 border-blue-600 text-blue-600 px-6 py-2.5 rounded-lg hover:bg-blue-50 transition-all font-semibold"
              >
                Register
              </Link>
              <Link
                to="/about"
                className="border-2 border-purple-600 text-purple-600 px-6 py-2.5 rounded-lg hover:bg-purple-50 transition-all font-semibold"
              >
                About
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-full text-sm font-semibold mb-8 shadow-sm">
              <div className="w-2 h-2 bg-blue-600 rounded-full mr-2 animate-pulse"></div>
              <span className="bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">
                Bohol Island State University - Official System
              </span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight mb-8 text-center">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent block mb-2 animate__animated animate__heartBeat">
                Share Your
              </span>
              <span className="bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent block animate__animated animate__heartBeat">
                Creative Works
              </span>
            </h1>
            
            <p className="text-2xl text-white/90 mb-12 leading-relaxed font-medium max-w-3xl mx-auto">
              The IMDC Creative Works Submission System empowers faculty members to submit,
              showcase, and archive their creative works including poems, short stories, visual
              arts, photography, and digital creations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Link
                to="/register"
                className="group bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl hover:shadow-2xl transition-all font-bold text-lg shadow-lg transform hover:-translate-y-1 flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Get Started Today
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                className="group flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors font-semibold text-lg"
              >
                Learn how it works
                <ChevronDown className="w-5 h-5 animate-bounce" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              FEATURES
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Everything You Need for Creative Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A comprehensive platform designed for academic creative works management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-blue-500/50 transition-shadow">
                <Upload className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Easy Submission</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Upload your creative works with our intuitive interface. Support for multiple formats and categories.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Drag and drop uploads</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Multiple file formats</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Instant preview</span>
                </li>
              </ul>
            </div>

            {/* Feature Card 2 */}
            <div className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-purple-300 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-purple-500/50 transition-shadow">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Track Progress</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Monitor your submission status in real-time with detailed tracking and notifications.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Real-time updates</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Admin feedback</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Status notifications</span>
                </li>
              </ul>
            </div>

            {/* Feature Card 3 */}
            <div className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-green-300 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-green-500/50 transition-shadow">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Secure Archive</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                All works are securely stored and preserved. Build your digital creative portfolio.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Cloud storage</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Digital portfolio</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Easy access</span>
                </li>
              </ul>
            </div>

            {/* Feature Card 4 */}
            <div className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-pink-300 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-pink-500/50 transition-shadow">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Admin Review</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Professional review and approval process by university administrators.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-pink-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Expert review</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-pink-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Quality assurance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-pink-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Constructive feedback</span>
                </li>
              </ul>
            </div>

            {/* Feature Card 5 */}
            <div className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-indigo-300 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-indigo-500/50 transition-shadow">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Recognition</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Get recognized for your creative contributions to the university community.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Achievement badges</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Public showcase</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Credit tracking</span>
                </li>
              </ul>
            </div>

            {/* Feature Card 6 */}
            <div className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-orange-300 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-orange-500/50 transition-shadow">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Fast & Efficient</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Streamlined workflow for quick submissions and approvals without hassle.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Quick processing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Automated workflow</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Bulk operations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-400/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              CATEGORIES
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Creative Work Categories</h2>
            <p className="text-xl text-gray-700 font-medium max-w-2xl mx-auto">
              Submit your work across multiple creative disciplines
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {/* Balak */}
            <div className="group bg-white border-2 border-rose-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-rose-500 to-pink-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
              <div className="aspect-[4/3] overflow-hidden [perspective:1000px]">
                <div className="relative w-full h-full category-sync-flip">
                  <div className="category-face absolute inset-0">
                    <ImageWithFallback 
                      src={imageAssets.categories.balak}
                      alt="Balak"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="category-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <ImageWithFallback 
                      src={imageAssets.categories.balak}
                      alt="Balak"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 text-center bg-rose-50">
                <div className="text-3xl mb-2">📜</div>
                <div className="font-bold text-gray-900 text-lg">Balak</div>
              </div>
            </div>

            {/* Children Story */}
            <div className="group bg-white border-2 border-amber-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
              <div className="aspect-[4/3] overflow-hidden [perspective:1000px]">
                <div className="relative w-full h-full category-sync-flip">
                  <div className="category-face absolute inset-0">
                    <ImageWithFallback 
                      src={imageAssets.categories.childrenStory}
                      alt="Children Story"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="category-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <ImageWithFallback 
                      src={imageAssets.categories.childrenStory}
                      alt="Children Story"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 text-center bg-amber-50">
                <div className="text-3xl mb-2">📚</div>
                <div className="font-bold text-gray-900 text-lg">Children Story</div>
              </div>
            </div>

            {/* Editorial Board */}
            <div className="group bg-white border-2 border-purple-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
              <div className="aspect-[4/3] overflow-hidden [perspective:1000px]">
                <div className="relative w-full h-full category-sync-flip">
                  <div className="category-face absolute inset-0">
                    <ImageWithFallback 
                      src={imageAssets.categories.editorialBoard}
                      alt="Editorial Board"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="category-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <ImageWithFallback 
                      src={imageAssets.categories.editorialBoard}
                      alt="Editorial Board"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 text-center bg-purple-50">
                <div className="text-3xl mb-2">📰</div>
                <div className="font-bold text-gray-900 text-lg">Editorial Board</div>
              </div>
            </div>

            {/* Fiction Adventure */}
            <div className="group bg-white border-2 border-blue-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
              <div className="aspect-[4/3] overflow-hidden [perspective:1000px]">
                <div className="relative w-full h-full category-sync-flip">
                  <div className="category-face absolute inset-0">
                    <ImageWithFallback 
                      src={imageAssets.categories.fictionAdventure}
                      alt="Fiction Adventure"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="category-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <ImageWithFallback 
                      src={imageAssets.categories.fictionAdventure}
                      alt="Fiction Adventure"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 text-center bg-blue-50">
                <div className="text-3xl mb-2">⚔️</div>
                <div className="font-bold text-gray-900 text-lg">Fiction Adventure</div>
              </div>
            </div>

            {/* Flash Fiction */}
            <div className="group bg-white border-2 border-green-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
              <div className="aspect-[4/3] overflow-hidden [perspective:1000px]">
                <div className="relative w-full h-full category-sync-flip">
                  <div className="category-face absolute inset-0">
                    <ImageWithFallback 
                      src={imageAssets.categories.flashFiction}
                      alt="Flash Fiction"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="category-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <ImageWithFallback 
                      src={imageAssets.categories.flashFiction}
                      alt="Flash Fiction"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 text-center bg-green-50">
                <div className="text-3xl mb-2">⚡</div>
                <div className="font-bold text-gray-900 text-lg">Flash Fiction</div>
              </div>
            </div>

            {/* Poetry */}
            <div className="group bg-white border-2 border-cyan-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
              <div className="aspect-[4/3] overflow-hidden [perspective:1000px]">
                <div className="relative w-full h-full category-sync-flip">
                  <div className="category-face absolute inset-0">
                    <ImageWithFallback 
                      src={imageAssets.categories.poetry}
                      alt="Poetry"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="category-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <ImageWithFallback 
                      src={imageAssets.categories.poetry}
                      alt="Poetry"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 text-center bg-cyan-50">
                <div className="text-3xl mb-2">📝</div>
                <div className="font-bold text-gray-900 text-lg">Poetry</div>
              </div>
            </div>

            {/* Tula */}
            <div className="group bg-white border-2 border-indigo-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-violet-500 opacity-0 group-hover:opacity-10 transition-opacity"></div>
              <div className="aspect-[4/3] overflow-hidden [perspective:1000px]">
                <div className="relative w-full h-full category-sync-flip">
                  <div className="category-face absolute inset-0">
                    <ImageWithFallback 
                      src={imageAssets.categories.tula}
                      alt="Tula"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="category-face absolute inset-0" style={{ transform: 'rotateY(180deg)' }}>
                    <ImageWithFallback 
                      src={imageAssets.categories.tula}
                      alt="Tula"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 text-center bg-indigo-50">
                <div className="text-3xl mb-2">✍️</div>
                <div className="font-bold text-gray-900 text-lg">Tula</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Share Your Creative Works?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join our growing community of creative faculty members and start building your digital portfolio today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="group bg-white text-purple-600 px-8 py-4 rounded-xl hover:bg-gray-100 transition-all font-bold text-lg shadow-lg transform hover:-translate-y-1 flex items-center justify-center gap-2"
            >
              <Send className="w-5 h-5" />
              Start Submitting
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/login"
              className="border-2 border-white text-white px-8 py-4 rounded-xl hover:bg-white/10 transition-all font-bold text-lg flex items-center justify-center gap-2"
            >
              <BookOpen className="w-5 h-5" />
              View Archive
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center space-x-3 mb-6">
                <img src={imageAssets.imdcLogo} alt="IMDC Logo" className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20" />
                <div>
                  <div className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    IMDC Creative Works
                  </div>
                  <div className="text-xs text-gray-400">BISU</div>
                </div>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Empowering faculty members to showcase their creative excellence through a modern digital platform.
              </p>
              <div className="flex space-x-4">
                <button type="button" aria-label="Facebook" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors">
                  <Facebook className="w-5 h-5" />
                </button>
                <button type="button" aria-label="Twitter" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-sky-500 transition-colors">
                  <Twitter className="w-5 h-5" />
                </button>
                <button type="button" aria-label="LinkedIn" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a></li>
                <li><Link to="/login" className="text-gray-400 hover:text-white transition-colors">Login</Link></li>
                <li><Link to="/register" className="text-gray-400 hover:text-white transition-colors">Register</Link></li>
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h4 className="text-lg font-bold mb-6">Categories</h4>
              <ul className="space-y-3">
                <li><span className="text-gray-400">Poetry</span></li>
                <li><span className="text-gray-400">Short Stories</span></li>
                <li><span className="text-gray-400">Visual Arts</span></li>
                <li><span className="text-gray-400">Photography</span></li>
                <li><span className="text-gray-400">Digital Art</span></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-400">Bohol Island State University, Tagbilaran City, Philippines</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-gray-400">(038) 411-2000</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-gray-400">imdc@bisu.edu.ph</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">
                © 2026 IMDC Creative Works. Bohol Island State University. All rights reserved.
              </p>
              <div className="flex gap-6">
                <button type="button" className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</button>
                <button type="button" className="text-gray-500 hover:text-white text-sm transition-colors">Terms of Service</button>
                <button type="button" className="text-gray-500 hover:text-white text-sm transition-colors">Support</button>
              </div>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}